import React, { useCallback, useMemo, useState } from "react";
import {
  Upload,
  FileDown,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  FileText,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";
import { processCSVFile, downloadCSVTemplate } from "../../utils/csvImportParser";

const ROWS_PER_PAGE = 20;

// ============================================================
// Step indicator
// ============================================================
function StepIndicator({ current, darkMode }) {
  const steps = [
    { n: 1, label: "Upload" },
    { n: 2, label: "Anteprima" },
    { n: 3, label: "Conferma" },
  ];
  return (
    <div className="flex items-center gap-2 mb-5">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-1.5">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                s.n === current
                  ? "bg-forfy-500 text-white"
                  : s.n < current
                  ? darkMode
                    ? "bg-emerald-700 text-emerald-200"
                    : "bg-emerald-100 text-emerald-700"
                  : darkMode
                  ? "bg-neutral-800 text-neutral-500"
                  : "bg-neutral-100 text-neutral-400"
              }`}
            >
              {s.n < current ? "✓" : s.n}
            </div>
            <span
              className={`text-xs hidden sm:inline ${
                s.n === current
                  ? darkMode
                    ? "text-white font-medium"
                    : "text-neutral-900 font-medium"
                  : darkMode
                  ? "text-neutral-500"
                  : "text-neutral-400"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              className={`flex-1 h-px ${
                s.n < current
                  ? darkMode
                    ? "bg-emerald-700"
                    : "bg-emerald-300"
                  : darkMode
                  ? "bg-neutral-800"
                  : "bg-neutral-200"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ============================================================
// Formatters
// ============================================================
function formatCurrency(value) {
  if (!Number.isFinite(value)) return "—";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const [intPart, decPart] = abs.toFixed(2).split(".");
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${groupedInt},${decPart} €`;
}

// ============================================================
// Main component
// ============================================================
export default function CsvImportModal({ open, onClose, onConfirm, darkMode = false, existingClients = [] }) {
  const [step, setStep] = useState(1);

  // Step 1 state
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  // Step 2 state
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Step 3 state
  const [createClients, setCreateClients] = useState(true);

  // Reset all state
  const resetState = useCallback(() => {
    setStep(1);
    setIsDragging(false);
    setFileName("");
    setParseError("");
    setIsLoading(false);
    setShowInstructions(false);
    setResult(null);
    setPage(0);
    setExpandedRows(new Set());
    setCreateClients(true);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  // Parse file
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setFileName(file.name);
    setParseError("");
    setIsLoading(true);

    try {
      const res = await processCSVFile(file, existingClients);
      if (!res.success) {
        setParseError(res.headerError);
        setIsLoading(false);
        return;
      }
      setResult(res);
      setPage(0);
      setExpandedRows(new Set());
      setStep(2);
    } catch (err) {
      setParseError(String(err?.message || err));
    } finally {
      setIsLoading(false);
    }
  }, [existingClients]);

  // Drag & drop handlers
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const file = e.dataTransfer?.files?.[0];
      if (file && (file.name.endsWith(".csv") || file.type === "text/csv")) {
        handleFile(file);
      } else {
        setParseError("Seleziona un file .csv valido.");
      }
    },
    [handleFile]
  );

  const onFileSelect = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  // Pagination
  const paginatedRows = useMemo(() => {
    if (!result?.rows) return [];
    const start = page * ROWS_PER_PAGE;
    return result.rows.slice(start, start + ROWS_PER_PAGE);
  }, [result, page]);

  const totalPages = result ? Math.ceil(result.rows.length / ROWS_PER_PAGE) : 0;

  // Importable rows
  const importableRows = useMemo(() => {
    if (!result?.rows) return [];
    return result.rows.filter((r) => r.isValid).map((r) => r.invoice);
  }, [result]);

  // Toggle expanded row
  const toggleExpanded = useCallback((lineNumber) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(lineNumber)) next.delete(lineNumber);
      else next.add(lineNumber);
      return next;
    });
  }, []);

  // Confirm import
  const handleImport = useCallback(() => {
    if (!result?.summary) return;
    onConfirm(importableRows, {
      createClients,
      newClients: result.summary.newClients,
    });
    resetState();
  }, [result, importableRows, createClients, onConfirm, resetState]);

  if (!open) return null;

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div
        className={`relative w-full sm:max-w-3xl max-h-[90vh] sm:max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl shadow-xl border ${
          darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200"
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-0">
          <div>
            <h3 className={`text-sm font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
              Importa CSV fatture
            </h3>
          </div>
          <button
            className={`p-2 rounded-lg ${
              darkMode ? "hover:bg-neutral-800 text-neutral-500" : "hover:bg-neutral-100 text-neutral-500"
            }`}
            onClick={handleClose}
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          <StepIndicator current={step} darkMode={darkMode} />

          {/* ==================== STEP 1: UPLOAD ==================== */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Drag & drop zone */}
              <div
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onClick={() => document.getElementById("csv-file-input")?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  isDragging
                    ? "border-forfy-500 bg-forfy-500/10"
                    : darkMode
                    ? "border-neutral-700 hover:border-neutral-600 bg-neutral-800/50"
                    : "border-neutral-300 hover:border-neutral-400 bg-neutral-50"
                }`}
              >
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={onFileSelect}
                  className="hidden"
                />
                <Upload
                  className={`w-8 h-8 mx-auto mb-3 ${
                    isDragging
                      ? "text-forfy-500"
                      : darkMode
                      ? "text-neutral-500"
                      : "text-neutral-400"
                  }`}
                />
                <p className={`text-sm font-medium ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>
                  {isLoading
                    ? "Analisi in corso..."
                    : isDragging
                    ? "Rilascia il file qui"
                    : "Trascina un file CSV qui"}
                </p>
                <p className={`text-xs mt-1 ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                  oppure clicca per selezionare
                </p>
                {fileName && !parseError && (
                  <div
                    className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs ${
                      darkMode ? "bg-neutral-800 text-neutral-300" : "bg-neutral-100 text-neutral-600"
                    }`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    {fileName}
                  </div>
                )}
              </div>

              {/* Error */}
              {parseError && (
                <div
                  className={`flex items-start gap-2 text-xs rounded-lg p-3 border ${
                    darkMode
                      ? "text-rose-400 bg-rose-900/30 border-rose-700"
                      : "text-rose-700 bg-rose-50 border-rose-200"
                  }`}
                >
                  <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">Errore: </span>
                    {parseError}
                  </div>
                </div>
              )}

              {/* Download template + instructions */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={downloadCSVTemplate}
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border ${
                    darkMode
                      ? "border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                      : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                  }`}
                >
                  <FileDown className="w-3.5 h-3.5" />
                  Scarica template CSV
                </button>

                <button
                  onClick={() => setShowInstructions(!showInstructions)}
                  className={`inline-flex items-center gap-1 text-xs ${
                    darkMode ? "text-neutral-400 hover:text-neutral-300" : "text-neutral-500 hover:text-neutral-700"
                  }`}
                >
                  {showInstructions ? (
                    <ChevronUp className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5" />
                  )}
                  Istruzioni
                </button>
              </div>

              {showInstructions && (
                <div
                  className={`text-xs rounded-lg p-3 border space-y-1.5 ${
                    darkMode
                      ? "bg-neutral-800 border-neutral-700 text-neutral-300"
                      : "bg-neutral-50 border-neutral-200 text-neutral-600"
                  }`}
                >
                  <p className="font-medium">Formato richiesto:</p>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>
                      <b>Separatore:</b> <code>;</code> o <code>,</code> (rilevato automaticamente)
                    </li>
                    <li>
                      <b>Date:</b> formato <code>dd/mm/yyyy</code>, <code>dd/mm/yy</code> o{" "}
                      <code>YYYY-MM-DD</code>
                    </li>
                    <li>
                      <b>Importi:</b> formato italiano con virgola decimale (es. <code>1.200,50</code>)
                    </li>
                    <li>
                      <b>Incassata:</b> <code>si</code>/<code>no</code> (opzionale — se assente, viene dedotta dalla
                      presenza di "Data pagamento")
                    </li>
                    <li>
                      <b>P.IVA Cliente:</b> opzionale, 11 cifre (con o senza prefisso IT)
                    </li>
                  </ul>
                  <p className="font-medium mt-2">Colonne obbligatorie:</p>
                  <p>Data emissione, Numero fattura, Cliente, Importo lordo</p>
                </div>
              )}
            </div>
          )}

          {/* ==================== STEP 2: PREVIEW ==================== */}
          {step === 2 && result && (
            <div className="space-y-4">
              {/* Summary badges */}
              <div className="flex flex-wrap items-center gap-2">
                {result.summary.validCount > 0 && (
                  <span
                    className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${
                      darkMode
                        ? "bg-emerald-900/40 text-emerald-400 border border-emerald-800"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    }`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {result.summary.validCount} valide
                  </span>
                )}
                {result.summary.warningCount > 0 && (
                  <span
                    className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${
                      darkMode
                        ? "bg-amber-900/40 text-amber-400 border border-amber-800"
                        : "bg-amber-50 text-amber-700 border border-amber-200"
                    }`}
                  >
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {result.summary.warningCount} con avvisi
                  </span>
                )}
                {result.summary.errorCount > 0 && (
                  <span
                    className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${
                      darkMode
                        ? "bg-rose-900/40 text-rose-400 border border-rose-800"
                        : "bg-rose-50 text-rose-700 border border-rose-200"
                    }`}
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    {result.summary.errorCount} con errori
                  </span>
                )}
              </div>

              {/* Table */}
              <div
                className={`rounded-xl border overflow-hidden ${
                  darkMode ? "border-neutral-800" : "border-neutral-200"
                }`}
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead
                      className={`text-xs ${
                        darkMode ? "bg-neutral-800 text-neutral-400" : "bg-neutral-50 text-neutral-600"
                      }`}
                    >
                      <tr>
                        <th className="text-center px-3 py-2 w-10">#</th>
                        <th className="text-center px-3 py-2 w-10">Stato</th>
                        <th className="text-left px-3 py-2">Numero</th>
                        <th className="text-left px-3 py-2">Data</th>
                        <th className="text-left px-3 py-2">Cliente</th>
                        <th className="text-right px-3 py-2">Importo</th>
                      </tr>
                    </thead>
                    <tbody className={darkMode ? "text-white" : "text-neutral-900"}>
                      {paginatedRows.map((row) => {
                        const hasWarnings = row.isValid && row.warnings.length > 0;
                        const hasErrors = !row.isValid;
                        const isExpanded = expandedRows.has(row.lineNumber);

                        return (
                          <React.Fragment key={row.lineNumber}>
                            <tr
                              className={`border-t cursor-pointer ${
                                darkMode ? "border-neutral-800" : "border-neutral-200"
                              } ${
                                hasErrors
                                  ? darkMode
                                    ? "bg-rose-900/10"
                                    : "bg-rose-50/50"
                                  : hasWarnings
                                  ? darkMode
                                    ? "bg-amber-900/10"
                                    : "bg-amber-50/50"
                                  : ""
                              }`}
                              onClick={() =>
                                (hasWarnings || hasErrors) && toggleExpanded(row.lineNumber)
                              }
                            >
                              <td
                                className={`text-center px-3 py-2 text-xs ${
                                  darkMode ? "text-neutral-500" : "text-neutral-400"
                                }`}
                              >
                                {row.lineNumber}
                              </td>
                              <td className="text-center px-3 py-2">
                                {hasErrors ? (
                                  <XCircle className="w-4 h-4 mx-auto text-rose-500" />
                                ) : hasWarnings ? (
                                  <AlertTriangle className="w-4 h-4 mx-auto text-amber-500" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4 mx-auto text-emerald-500" />
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {row.invoice?.invoiceCode || row.clientName || "—"}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {row.invoice?.issueDate || "—"}
                              </td>
                              <td className="px-3 py-2 max-w-[180px] truncate">
                                {row.invoice?.clientName || row.clientName || "—"}
                              </td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                {row.invoice ? formatCurrency(row.invoice.grossAmount) : "—"}
                              </td>
                            </tr>

                            {/* Expanded detail row */}
                            {isExpanded && (hasWarnings || hasErrors) && (
                              <tr
                                className={`border-t ${
                                  darkMode ? "border-neutral-800" : "border-neutral-200"
                                }`}
                              >
                                <td colSpan={6} className="px-3 py-2">
                                  <div className="space-y-1 text-xs pl-6">
                                    {row.errors.map((e, i) => (
                                      <div
                                        key={`err-${i}`}
                                        className={`flex items-start gap-1.5 ${
                                          darkMode ? "text-rose-400" : "text-rose-600"
                                        }`}
                                      >
                                        <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                        {e}
                                      </div>
                                    ))}
                                    {row.warnings.map((w, i) => (
                                      <div
                                        key={`warn-${i}`}
                                        className={`flex items-start gap-1.5 ${
                                          darkMode ? "text-amber-400" : "text-amber-600"
                                        }`}
                                      >
                                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                        {w}
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                    Pagina {page + 1} di {totalPages}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                      className={`p-1.5 rounded-lg disabled:opacity-30 ${
                        darkMode ? "hover:bg-neutral-800 text-neutral-400" : "hover:bg-neutral-100 text-neutral-600"
                      }`}
                      aria-label="Pagina precedente"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                      className={`p-1.5 rounded-lg disabled:opacity-30 ${
                        darkMode ? "hover:bg-neutral-800 text-neutral-400" : "hover:bg-neutral-100 text-neutral-600"
                      }`}
                      aria-label="Pagina successiva"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => {
                    setStep(1);
                    setResult(null);
                    setFileName("");
                    setParseError("");
                  }}
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg border text-sm ${
                    darkMode
                      ? "border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                      : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Indietro
                </button>
                <button
                  disabled={importableRows.length === 0}
                  onClick={() => setStep(3)}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600 disabled:opacity-50"
                >
                  Continua
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ==================== STEP 3: CONFIRM ==================== */}
          {step === 3 && result && (
            <div className="space-y-4">
              {/* Summary */}
              <div
                className={`rounded-xl border p-4 space-y-3 ${
                  darkMode ? "bg-neutral-800 border-neutral-700" : "bg-neutral-50 border-neutral-200"
                }`}
              >
                <h4 className={`text-sm font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                  Riepilogo importazione
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                      Fatture da importare
                    </span>
                    <div className={`font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
                      {result.summary.importableCount}
                    </div>
                  </div>
                  <div>
                    <span className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                      Totale importo
                    </span>
                    <div className={`font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
                      {formatCurrency(result.summary.totalAmount)}
                    </div>
                  </div>
                  <div>
                    <span className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                      Incassate
                    </span>
                    <div className={`font-semibold ${darkMode ? "text-emerald-400" : "text-emerald-600"}`}>
                      {result.summary.paidCount}
                    </div>
                  </div>
                  <div>
                    <span className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                      Da incassare
                    </span>
                    <div className={`font-semibold ${darkMode ? "text-amber-400" : "text-amber-600"}`}>
                      {result.summary.unpaidCount}
                    </div>
                  </div>
                </div>

                {result.summary.errorCount > 0 && (
                  <div
                    className={`text-xs rounded-lg p-2 ${
                      darkMode
                        ? "bg-rose-900/30 text-rose-400 border border-rose-800"
                        : "bg-rose-50 text-rose-700 border border-rose-200"
                    }`}
                  >
                    {result.summary.errorCount} righe con errori saranno escluse dall'importazione.
                  </div>
                )}
              </div>

              {/* New clients */}
              {result.summary.newClients.length > 0 && (
                <div
                  className={`rounded-xl border p-4 space-y-3 ${
                    darkMode ? "bg-neutral-800 border-neutral-700" : "bg-neutral-50 border-neutral-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h4 className={`text-sm font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                      Nuovi clienti ({result.summary.newClients.length})
                    </h4>
                  </div>
                  <div className="space-y-1.5">
                    {result.summary.newClients.map((c) => (
                      <div
                        key={c.name}
                        className={`flex items-center justify-between text-xs px-3 py-2 rounded-lg ${
                          darkMode ? "bg-neutral-900 text-neutral-300" : "bg-white text-neutral-700"
                        }`}
                      >
                        <span className="font-medium">{c.name}</span>
                        {c.vatNumber && (
                          <span className={darkMode ? "text-neutral-500" : "text-neutral-400"}>
                            P.IVA: {c.vatNumber}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <label
                    className={`flex items-center gap-2 text-xs cursor-pointer ${
                      darkMode ? "text-neutral-300" : "text-neutral-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={createClients}
                      onChange={(e) => setCreateClients(e.target.checked)}
                      className="rounded"
                    />
                    Crea automaticamente i clienti mancanti nell'anagrafica
                  </label>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setStep(2)}
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg border text-sm ${
                    darkMode
                      ? "border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                      : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Indietro
                </button>
                <button
                  onClick={handleImport}
                  disabled={importableRows.length === 0}
                  className="px-4 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600 disabled:opacity-50 font-medium"
                >
                  Importa {importableRows.length} fatture
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
